import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, Trophy, Plus, Loader2, ArrowRight, X, Lock } from "lucide-react";
import SpeakerButton from "./SpeakerButton";
import { fetchAllCadets, createCadetAccount, login, setActiveCadetId, getActiveCadetId } from "../lib/api";

export const CadetPicker = ({ onSelected }) => {
  const [cadets, setCadets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedCallsign, setPickedCallsign] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("select"); // 'select' | 'create'
  const [newCallsign, setNewCallsign] = useState("");
  const [newPin, setNewPin] = useState("");

  useEffect(() => {
    fetchAllCadets().then(setCadets).catch(() => setCadets([])).finally(() => setLoading(false));
  }, []);

  const tryLogin = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await login(pickedCallsign, pin);
      setActiveCadetId(res.id);
      onSelected && onSelected(res);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Check PIN.");
    } finally { setBusy(false); }
  };

  const tryCreate = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const created = await createCadetAccount(newCallsign.trim(), newPin.trim());
      const res = await login(newCallsign.trim(), newPin.trim());
      setActiveCadetId(res.id);
      onSelected && onSelected(res);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not charter cadet.");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="fixed inset-0 z-[100] eigoo-stage-bg flex items-center justify-center p-4"
        data-testid="cadet-picker"
      >
        <motion.div
          initial={{ scale: 0.95, y: 14 }} animate={{ scale: 1, y: 0 }}
          className="eigoo-glass rounded-3xl p-7 md:p-10 w-full max-w-2xl"
        >
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-yellow-300/30 bg-yellow-300/5 text-yellow-300 font-display font-bold tracking-[0.3em] text-[10px] mb-3">
              <Radio size={12} /> CYBERKIDS · SOVEREIGN FLEET
            </div>
            <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight">
              <span className="bg-gradient-to-b from-yellow-100 via-yellow-300 to-yellow-600 bg-clip-text text-transparent">Identify Yourself, Cadet</span>
            </h1>
            <div className="flex items-center justify-center gap-2 mt-3">
              <p className="text-white/70">Pick your callsign and enter your PIN. Or charter a new cadet.</p>
              <SpeakerButton text="Welcome to CyberKids Academy. Pick your callsign and type your four digit pin. Or charter a new cadet to begin." testid="speaker-picker" size="sm" />
            </div>
          </div>

          {mode === "select" ? (
            <>
              {loading ? (
                <div className="text-center text-white/60 py-8">Loading roster...</div>
              ) : (
                <ul className="grid sm:grid-cols-2 gap-3 mb-5">
                  {cadets.map(c => (
                    <li key={c.id}>
                      <button
                        onClick={() => { setPickedCallsign(c.callsign); setError(""); }}
                        className={`w-full text-left p-4 rounded-2xl border transition-all ${
                          pickedCallsign === c.callsign
                            ? "bg-yellow-300/10 border-yellow-300/50 eigoo-glow-gold"
                            : "bg-black/30 border-white/10 hover:border-yellow-300/30"
                        }`}
                        data-testid={`pick-cadet-${c.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-yellow-300 to-yellow-600 text-black flex items-center justify-center font-display font-black text-lg">
                            {c.callsign?.[0] || "C"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold truncate">{c.callsign}</div>
                            <div className="text-xs text-yellow-300/80 flex items-center gap-2">
                              <Trophy size={10} /> {c.rank} · {c.xp} XP
                            </div>
                          </div>
                          <Lock size={14} className="text-white/30 shrink-0" />
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {pickedCallsign && (
                <form onSubmit={tryLogin} className="space-y-3">
                  <div className="text-sm text-yellow-200">
                    Enter PIN for <span className="font-bold">{pickedCallsign}</span>
                    {pickedCallsign === "Commander Dalvin" && <span className="text-white/50 ml-2">(default PIN: 0001)</span>}
                  </div>
                  <input
                    autoFocus
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g,""))}
                    placeholder="• • • •"
                    className="w-full text-center font-display font-black text-3xl tracking-[0.5em] px-4 py-4 rounded-2xl bg-black/40 border border-yellow-300/30 focus:border-yellow-300/70 outline-none"
                    data-testid="pin-input"
                  />
                  {error && <div className="text-red-300 text-sm" data-testid="login-error">{error}</div>}
                  <button type="submit" disabled={busy || pin.length !== 4} className="btn-eigoo btn-gold w-full" data-testid="login-submit">
                    {busy ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
                    {busy ? "Boarding..." : "Board the Bridge"}
                  </button>
                </form>
              )}
              <div className="mt-5 text-center">
                <button onClick={() => { setMode("create"); setError(""); }} className="text-sm text-yellow-300 hover:underline" data-testid="open-charter">
                  <Plus size={14} className="inline mr-1" /> Charter a new cadet
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={tryCreate} className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-lg">Charter a New Cadet</div>
                <button type="button" onClick={() => { setMode("select"); setError(""); }} className="text-white/50 hover:text-white"><X size={18} /></button>
              </div>
              <input
                value={newCallsign}
                onChange={(e) => setNewCallsign(e.target.value)}
                placeholder="Callsign (e.g. Commander Mira)"
                className="w-full px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/30 outline-none text-base"
                data-testid="new-callsign"
                required
              />
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g,""))}
                placeholder="4-digit PIN"
                className="w-full text-center font-display font-black text-2xl tracking-[0.4em] px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/30 outline-none"
                data-testid="new-pin"
                required
              />
              {error && <div className="text-red-300 text-sm" data-testid="create-error">{error}</div>}
              <button type="submit" disabled={busy || newPin.length !== 4 || !newCallsign.trim()} className="btn-eigoo btn-gold w-full" data-testid="create-submit">
                {busy ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
                {busy ? "Chartering..." : "Charter Cadet"}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CadetPicker;
